<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, provide, ref, type CSSProperties, type VNodeChild } from 'vue'
import RenderContent from '../internal/RenderContent'
import { descriptionsKey } from './context'
import { useUiConfig } from '../config-provider/context'

type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'
const props = withDefaults(defineProps<{
  title?: VNodeChild
  extra?: VNodeChild
  column?: number | Partial<Record<Breakpoint, number>>
  bordered?: boolean
  colon?: boolean
  layout?: 'horizontal' | 'vertical'
  size?: 'default' | 'middle' | 'small'
  labelStyle?: CSSProperties
  contentStyle?: CSSProperties
}>(), { column: 3, bordered: false, colon: true, layout: 'horizontal' })
const config = useUiConfig()
const viewportWidth = ref(0)
const breakpoints: [Breakpoint, number][] = [['xxl', 1600], ['xl', 1200], ['lg', 992], ['md', 768], ['sm', 576], ['xs', 0]]
const columns = computed(() => {
  const columns = props.column
  if (typeof columns === 'number') return Math.max(1, Math.trunc(columns))
  const breakpoint = breakpoints.find(([key, min]) => viewportWidth.value >= min && columns[key] !== undefined)
  return Math.max(1, Math.trunc(breakpoint ? columns[breakpoint[0]]! : columns.xs ?? 1))
})
function resize() { viewportWidth.value = window.innerWidth }
onMounted(() => { resize(); window.addEventListener('resize', resize) })
onBeforeUnmount(() => window.removeEventListener('resize', resize))
provide(descriptionsKey, computed(() => ({ column: columns.value, colon: props.colon, labelStyle: props.labelStyle, contentStyle: props.contentStyle })))
</script>

<template>
  <section class="rr-descriptions" :class="[`rr-descriptions--${layout}`, `rr-descriptions--${size ?? config.componentSize ?? 'default'}`, { 'is-bordered': bordered }]">
    <header v-if="title || extra || $slots.title || $slots.extra" class="rr-descriptions__header"><h3 v-if="title || $slots.title"><slot name="title"><RenderContent :value="title" /></slot></h3><div v-if="extra || $slots.extra" class="rr-descriptions__extra"><slot name="extra"><RenderContent :value="extra" /></slot></div></header>
    <dl class="rr-descriptions__list" :style="{ '--rr-description-columns': columns }"><slot /></dl>
  </section>
</template>

<style>
.rr-descriptions { min-width: 0; color: var(--rr-color-text); font-size: var(--rr-font-size-base); }
.rr-descriptions__header { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: var(--rr-space-3); margin-bottom: var(--rr-space-3); }
.rr-descriptions__header h3 { margin: 0; color: var(--rr-color-text-strong); font-size: var(--rr-font-size-md); font-weight: 600; }
.rr-descriptions__list { display: grid; grid-template-columns: repeat(var(--rr-description-columns), minmax(0, 1fr)); margin: 0; }
.rr-descriptions__item { display: flex; align-items: baseline; min-width: 0; margin: 0; padding: var(--rr-space-2) var(--rr-space-3) var(--rr-space-2) 0; gap: var(--rr-space-2); overflow-wrap: anywhere; }
.rr-descriptions__label { flex-shrink: 0; max-width: 45%; color: var(--rr-color-text-muted); font-weight: 400; }
.rr-descriptions__label.has-colon::after { content: ':'; margin-left: 2px; }
.rr-descriptions__content { flex: 1; min-width: 0; margin: 0; overflow-wrap: anywhere; }
.rr-descriptions--vertical .rr-descriptions__item { flex-direction: column; align-items: stretch; }
.rr-descriptions--vertical .rr-descriptions__label { max-width: none; }
.rr-descriptions.is-bordered .rr-descriptions__list { border-top: 1px solid var(--rr-color-border); border-left: 1px solid var(--rr-color-border); border-radius: var(--rr-radius-control); overflow: hidden; }
.rr-descriptions.is-bordered .rr-descriptions__item { padding: 0; gap: 0; align-items: stretch; border-right: 1px solid var(--rr-color-border); border-bottom: 1px solid var(--rr-color-border); }
.rr-descriptions.is-bordered .rr-descriptions__label, .rr-descriptions.is-bordered .rr-descriptions__content { padding: var(--rr-space-3); }
.rr-descriptions.is-bordered .rr-descriptions__label { background: var(--rr-color-surface-soft); }
.rr-descriptions--small .rr-descriptions__item { padding-top: var(--rr-space-1); padding-bottom: var(--rr-space-1); }
.rr-descriptions--small.is-bordered .rr-descriptions__label, .rr-descriptions--small.is-bordered .rr-descriptions__content { padding: var(--rr-space-2); }
</style>
